import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AreaChartIcon from '@mui/icons-material/AreaChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutlineOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import {
  LAYOUT_META,
  createSmartBlock,
  getFavoriteLayouts,
  toggleFavoriteLayout,
} from '../lib/layouts';
import { useDeckStore } from '../store/deckStore';
import { api } from '../lib/api';
import type { ChartType, CompanyAsset, MasterLayoutId, SlideElement, SmartBlockKind } from '../types/slide';
import { ASSETS, BRAND, FONT_STACKS } from '../lib/brand';
import { uid as makeId } from '../lib/id';
import { LayoutThumb } from './LayoutThumb';

type UnsplashPhoto = Awaited<ReturnType<typeof api.searchUnsplash>>['photos'][number];

const UNSPLASH_PRESETS = ['office', 'technology', 'team meeting', 'city skyline', 'abstract blue'] as const;

type Tab = 'layouts' | 'text' | 'blocks' | 'media' | 'charts';

const TABS: { id: Tab; icon: typeof DashboardOutlinedIcon; label: string }[] = [
  { id: 'layouts', icon: DashboardOutlinedIcon, label: 'Layouts' },
  { id: 'text', icon: TextFieldsIcon, label: 'Text' },
  { id: 'blocks', icon: WidgetsOutlinedIcon, label: 'Blocks' },
  { id: 'media', icon: ImageOutlinedIcon, label: 'Media' },
  { id: 'charts', icon: InsertChartOutlinedIcon, label: 'Charts' },
];

/** Text presets in the AST template's content styles. */
const TEXT_STYLES = [
  {
    name: 'Display title',
    fontSize: 56,
    fontWeight: 700,
    color: BRAND.blue,
    w: 900,
    h: 88,
    sample: 'Add a display title',
    font: 'display' as const,
  },
  {
    name: 'Header',
    fontSize: 36,
    fontWeight: 700,
    color: BRAND.bright,
    w: 760,
    h: 52,
    sample: 'Add a header',
    font: 'body' as const,
  },
  {
    name: 'Subheading',
    fontSize: 28,
    fontWeight: 700,
    color: BRAND.blue,
    w: 640,
    h: 44,
    sample: 'Add a subheading',
    font: 'body' as const,
  },
  {
    name: 'Body text',
    fontSize: 25,
    fontWeight: 400,
    color: BRAND.ink,
    w: 560,
    h: 120,
    sample: 'Add body text',
    font: 'body' as const,
  },
  {
    name: 'Bullet list',
    fontSize: 25,
    fontWeight: 400,
    color: BRAND.ink,
    w: 560,
    h: 160,
    sample: '• First point\n\n• Second point\n\n• Third point',
    font: 'body' as const,
  },
  {
    name: 'Caption',
    fontSize: 16,
    fontWeight: 500,
    color: BRAND.gray,
    w: 420,
    h: 36,
    sample: 'Supporting caption',
    font: 'label' as const,
  },
  {
    name: 'Label',
    fontSize: 14,
    fontWeight: 600,
    color: BRAND.bright,
    w: 280,
    h: 28,
    sample: 'SECTION LABEL',
    font: 'label' as const,
  },
  {
    name: 'Quote',
    fontSize: 28,
    fontWeight: 600,
    color: BRAND.ink,
    w: 720,
    h: 100,
    sample: '“A short executive quote.”',
    font: 'display' as const,
  },
] as const;

const BLOCKS: { kind: SmartBlockKind; name: string; hint: string }[] = [
  { kind: 'kpi_card', name: 'KPI metric card', hint: 'Big number with label' },
  { kind: 'stat_row', name: 'Stat row', hint: 'Four compact metrics' },
  { kind: 'pros_cons', name: 'Pros / cons', hint: 'Two-column trade-offs' },
  { kind: 'callout', name: 'Callout banner', hint: 'Decision or alert strip' },
  { kind: 'quote_block', name: 'Quote block', hint: 'Quote with attribution' },
  { kind: 'checklist', name: 'Checklist', hint: 'Actionable to-dos' },
  { kind: 'process_row', name: '4-step process', hint: 'Horizontal journey' },
  { kind: 'team_card', name: 'Team card', hint: 'Name, role, contact' },
];

const BRAND_MEDIA = [
  { id: 'logo', name: 'AST logo', src: ASSETS.logo },
  { id: 'logo_white', name: 'AST logo (white)', src: ASSETS.logoWhite },
  { id: 'side_strip', name: 'Side strip photo', src: ASSETS.sideStrip },
  { id: 'cover_bg', name: 'Cover background', src: ASSETS.coverBackground },
] as const;

const SHAPES = [
  { name: 'Blue panel', fill: BRAND.blue, w: 360, h: 200, radius: 0 },
  { name: 'Surface card', fill: BRAND.surface, w: 360, h: 200, radius: 8 },
  { name: 'Accent bar', fill: BRAND.bright, w: 480, h: 12, radius: 0 },
  { name: 'Sky chip', fill: BRAND.sky, w: 160, h: 48, radius: 8 },
] as const;

const CHART_TYPES: { id: ChartType; label: string; icon: typeof BarChartIcon }[] = [
  { id: 'bar', label: 'Bar', icon: BarChartIcon },
  { id: 'hbar', label: 'H-Bar', icon: AlignHorizontalLeftIcon },
  { id: 'line', label: 'Line', icon: ShowChartIcon },
  { id: 'area', label: 'Area', icon: AreaChartIcon },
  { id: 'donut', label: 'Donut', icon: DonutLargeIcon },
  { id: 'pie', label: 'Pie', icon: PieChartOutlineIcon },
];

function PanelHeader({ title, meta }: { title: string; meta?: string }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 1.75 }}>
      <Typography variant="subtitle2" component="h2">
        {title}
      </Typography>
      {meta && <Typography variant="caption">{meta}</Typography>}
    </Stack>
  );
}

/** Bordered, clickable row used for blocks and text styles. */
function OptionRow({
  children,
  onClick,
  draggable,
  onDragStart,
}: {
  children: React.ReactNode;
  onClick: () => void;
  draggable?: boolean;
  onDragStart?: React.DragEventHandler;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        px: 1.5,
        py: 1.25,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': { borderColor: 'chrome.border', bgcolor: 'chrome.subtle' },
      }}
    >
      {children}
    </ButtonBase>
  );
}

export function LeftSidebar() {
  const [tab, setTab] = useState<Tab>('layouts');
  const [query, setQuery] = useState('');
  const [favorites, setFavorites] = useState<MasterLayoutId[]>(() => getFavoriteLayouts());
  const applyLayout = useDeckStore((s) => s.applyLayout);
  const addElement = useDeckStore((s) => s.addElement);
  const activeLayout = useDeckStore((s) => s.slides[s.activeSlideIndex]?.layoutId);
  const [assets, setAssets] = useState<CompanyAsset[]>([]);
  const [csv, setCsv] = useState('Week,Value\nW1,12\nW2,18\nW3,15\nW4,22');
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [unsplashQuery, setUnsplashQuery] = useState('office');
  const [unsplashPhotos, setUnsplashPhotos] = useState<UnsplashPhoto[]>([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [unsplashError, setUnsplashError] = useState<string | null>(null);
  const [unsplashInserting, setUnsplashInserting] = useState<string | null>(null);

  const layouts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = LAYOUT_META.filter(
      (l) => l.name.toLowerCase().includes(q) || l.description.toLowerCase().includes(q),
    );
    const favSet = new Set(favorites);
    return [...filtered].sort((a, b) => {
      const af = favSet.has(a.id) ? 0 : 1;
      const bf = favSet.has(b.id) ? 0 : 1;
      if (af !== bf) return af - bf;
      return 0;
    });
  }, [query, favorites]);

  const runUnsplashSearch = async (q: string) => {
    const term = q.trim();
    if (!term) return;
    setUnsplashLoading(true);
    setUnsplashError(null);
    try {
      const res = await api.searchUnsplash(term);
      setUnsplashPhotos(res.photos);
      if (!res.photos.length) setUnsplashError('No photos matched that search.');
    } catch (err) {
      setUnsplashPhotos([]);
      setUnsplashError(err instanceof Error ? err.message : 'Unsplash search failed');
    } finally {
      setUnsplashLoading(false);
    }
  };

  useEffect(() => {
    if (tab !== 'media') return;
    void api
      .listAssets()
      .then((r) => setAssets(r.assets))
      .catch(() => setAssets([]));
    if (!unsplashPhotos.length && !unsplashError) {
      void runUnsplashSearch(unsplashQuery);
    }
    // Initial Unsplash load only when opening Media.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const addImage = (src: string, opts?: { w?: number; h?: number; fit?: 'cover' | 'contain' }) =>
    addElement({
      id: makeId('img_'),
      type: 'image',
      x: 100,
      y: 160,
      w: opts?.w ?? 480,
      h: opts?.h ?? 320,
      src,
      fit: opts?.fit ?? 'cover',
      radius: 8,
    });

  const addText = (style: (typeof TEXT_STYLES)[number]) => {
    const el: SlideElement = {
      id: makeId('text_'),
      type: 'text',
      x: 80,
      y: 200,
      w: style.w,
      h: style.h,
      text: style.sample,
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      color: style.color,
      font: style.font,
      align: 'left',
    };
    addElement(el);
  };

  const addShape = (shape: (typeof SHAPES)[number]) => {
    addElement({
      id: makeId('shape_'),
      type: 'shape',
      name: shape.name,
      x: 120,
      y: 200,
      w: shape.w,
      h: shape.h,
      fill: shape.fill,
      radius: shape.radius,
    });
  };

  const parseCsv = () => {
    const lines = csv.trim().split(/\n+/).slice(1);
    const labels: string[] = [];
    const values: number[] = [];
    for (const line of lines) {
      const [l, v] = line.split(',').map((x) => x.trim());
      if (!l) continue;
      labels.push(l);
      values.push(Number(v) || 0);
    }
    addElement({
      id: makeId('chart_'),
      type: 'chart',
      x: 80,
      y: 180,
      w: 720,
      h: 400,
      chartType,
      chartLabels: labels,
      chartValues: values,
      fill: BRAND.surface,
      radius: 12,
    });
  };

  const onUpload = async (file: File) => {
    try {
      // Personal R2 upload — not the curated company library (admins use Assets page).
      const uploaded = await api.uploadSlideMedia(file);
      addImage(uploaded.r2Url);
    } catch {
      // Offline / R2 off: embed as data URL so the slide still works.
      const reader = new FileReader();
      reader.onload = () => addImage(String(reader.result));
      reader.readAsDataURL(file);
    }
  };

  const onInsertUnsplash = async (photo: UnsplashPhoto) => {
    setUnsplashInserting(photo.id);
    setUnsplashError(null);
    try {
      const inserted = await api.insertUnsplash({
        downloadLocation: photo.downloadLocation,
        imageUrl: photo.imageUrl,
        alt: photo.alt,
        photographer: photo.photographer,
      });
      addImage(inserted.imageUrl, { w: 640, h: 400 });
    } catch (err) {
      setUnsplashError(err instanceof Error ? err.message : 'Could not insert Unsplash photo');
    } finally {
      setUnsplashInserting(null);
    }
  };

  const onToggleFavorite = (id: MasterLayoutId, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(toggleFavoriteLayout(id));
  };

  return (
    <Box
      component="aside"
      sx={{ display: 'flex', flexShrink: 0, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider' }}
    >
      <Stack
        component="nav"
        aria-label="Insert"
        spacing={0.5}
        sx={{ width: 72, p: 1, borderRight: 1, borderColor: 'chrome.subtle' }}
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <ButtonBase
              key={t.id}
              aria-pressed={active}
              onClick={() => setTab(t.id)}
              sx={{
                flexDirection: 'column',
                gap: 0.5,
                py: 1.25,
                borderRadius: 2,
                fontSize: 11,
                fontWeight: 500,
                color: active ? 'primary.main' : 'text.secondary',
                bgcolor: active ? 'chrome.tint' : 'transparent',
                '&:hover': { bgcolor: active ? 'chrome.tint' : 'chrome.subtle' },
              }}
            >
              <t.icon sx={{ fontSize: 20 }} />
              {t.label}
            </ButtonBase>
          );
        })}
      </Stack>

      <Box sx={{ width: 264, overflowY: 'auto', p: 2 }}>
        {tab === 'layouts' && (
          <>
            <PanelHeader title="Layouts" meta={`${LAYOUT_META.length} masters`} />
            <TextField
              fullWidth
              placeholder="Search layouts"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              slotProps={{
                htmlInput: { 'aria-label': 'Search layouts' },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ mb: 1 }}
            />
            {favorites.length > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mb: 1.25, color: 'text.secondary' }}>
                Star layouts you prefer — they sort to the top.
              </Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
              {layouts.map((l) => {
                const active = activeLayout === l.id;
                const fav = favorites.includes(l.id);
                return (
                  <Box key={l.id} sx={{ position: 'relative' }}>
                    <ButtonBase
                      onClick={() => applyLayout(l.id as MasterLayoutId)}
                      title={l.description}
                      sx={{
                        width: '100%',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        gap: 0.75,
                        textAlign: 'left',
                      }}
                    >
                      <Box
                        sx={(t) => ({
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '1.5px solid',
                          borderColor: active ? 'primary.main' : 'divider',
                          boxShadow: active ? `0 0 0 3px ${alpha(t.palette.primary.main, 0.15)}` : 'none',
                          transition: 'border-color 120ms',
                          '.MuiButtonBase-root:hover &': { borderColor: active ? 'primary.main' : 'chrome.border' },
                        })}
                      >
                        <LayoutThumb id={l.id} />
                      </Box>
                      <Typography variant="caption" noWrap sx={{ fontWeight: 500, color: 'neutral.main', pr: 3 }}>
                        {l.name}
                      </Typography>
                    </ButtonBase>
                    <Tooltip title={fav ? 'Remove from favorites' : 'Pin as preferred layout'}>
                      <IconButton
                        size="small"
                        aria-label={fav ? `Unpin ${l.name}` : `Pin ${l.name}`}
                        onClick={(e) => onToggleFavorite(l.id, e)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255,255,255,0.92)',
                          boxShadow: '0 1px 2px rgba(16,24,40,0.12)',
                          '&:hover': { bgcolor: 'common.white' },
                        }}
                      >
                        {fav ? (
                          <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        ) : (
                          <StarBorderIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
            {!layouts.length && <Typography variant="caption">No layouts match “{query}”.</Typography>}
          </>
        )}

        {tab === 'text' && (
          <>
            <PanelHeader title="Text" meta={`${TEXT_STYLES.length} styles`} />
            <Stack spacing={1}>
              {TEXT_STYLES.map((s) => (
                <OptionRow key={s.name} onClick={() => addText(s)}>
                  <Typography
                    sx={{
                      fontFamily: FONT_STACKS[s.font],
                      fontSize: Math.min(22, Math.round(s.fontSize / 2.2) + 4),
                      fontWeight: s.fontWeight,
                      color: s.color,
                      lineHeight: 1.3,
                    }}
                  >
                    {s.name}
                  </Typography>
                </OptionRow>
              ))}
            </Stack>
          </>
        )}

        {tab === 'blocks' && (
          <>
            <PanelHeader title="Blocks" meta="Drag or click" />
            <Stack spacing={1}>
              {BLOCKS.map((b) => (
                <OptionRow
                  key={b.kind}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('smart-block', b.kind)}
                  onClick={() => addElement(createSmartBlock(b.kind, { x: 120, y: 200 }))}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {b.name}
                  </Typography>
                  <Typography variant="caption">{b.hint}</Typography>
                </OptionRow>
              ))}
            </Stack>
          </>
        )}

        {tab === 'media' && (
          <>
            <PanelHeader title="Media" meta="Unsplash + brand" />

            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Unsplash
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Unsplash"
              value={unsplashQuery}
              onChange={(e) => setUnsplashQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void runUnsplashSearch(unsplashQuery);
              }}
              slotProps={{
                htmlInput: { 'aria-label': 'Search Unsplash' },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: unsplashLoading ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : undefined,
                },
              }}
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.25 }}>
              {UNSPLASH_PRESETS.map((p) => (
                <Button
                  key={p}
                  size="small"
                  variant={unsplashQuery === p ? 'contained' : 'outlined'}
                  color="neutral"
                  onClick={() => {
                    setUnsplashQuery(p);
                    void runUnsplashSearch(p);
                  }}
                  sx={{ textTransform: 'none', fontSize: 11, py: 0.25, minWidth: 0 }}
                >
                  {p}
                </Button>
              ))}
            </Stack>
            <Button
              fullWidth
              variant="contained"
              size="small"
              disabled={unsplashLoading || !unsplashQuery.trim()}
              onClick={() => void runUnsplashSearch(unsplashQuery)}
              sx={{ mb: 1.5 }}
            >
              Search photos
            </Button>
            {unsplashError && (
              <Typography variant="caption" color="error" component="p" sx={{ mb: 1 }}>
                {unsplashError}
              </Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, mb: 1 }}>
              {unsplashPhotos.map((photo) => {
                const busy = unsplashInserting === photo.id;
                return (
                  <ButtonBase
                    key={photo.id}
                    disabled={Boolean(unsplashInserting)}
                    onClick={() => void onInsertUnsplash(photo)}
                    title={`${photo.alt} — Photo by ${photo.photographer}`}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                      gap: 0.5,
                      textAlign: 'left',
                      opacity: unsplashInserting && !busy ? 0.55 : 1,
                    }}
                  >
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <Box
                        component="img"
                        src={photo.thumbUrl}
                        alt={photo.alt}
                        sx={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          borderRadius: 1.5,
                          border: 1,
                          borderColor: 'divider',
                          bgcolor: photo.color,
                        }}
                      />
                      {busy && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255,255,255,0.55)',
                            borderRadius: 1.5,
                          }}
                        >
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </Box>
                    <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                      {photo.photographer}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>
            <Typography variant="caption" component="p" sx={{ mb: 2, color: 'text.secondary' }}>
              Photos from{' '}
              <Link
                href="https://unsplash.com/?utm_source=slidesprint&utm_medium=referral"
                target="_blank"
                rel="noreferrer"
                underline="hover"
              >
                Unsplash
              </Link>
              . Click a photo to add it to the slide.
            </Typography>

            <Button
              component="label"
              variant="outlined"
              color="neutral"
              fullWidth
              startIcon={<CloudUploadOutlinedIcon />}
              sx={{ height: 72, borderStyle: 'dashed', mb: 2 }}
            >
              Upload image
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onUpload(f);
                }}
              />
            </Button>

            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Brand assets
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, mb: 2 }}>
              {BRAND_MEDIA.map((a) => (
                <ButtonBase
                  key={a.id}
                  onClick={() =>
                    addImage(a.src, {
                      w: a.id.includes('logo') ? 240 : 480,
                      h: a.id.includes('logo') ? 88 : 320,
                      fit: a.id.includes('logo') ? 'contain' : 'cover',
                    })
                  }
                  sx={{ flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', gap: 0.75, textAlign: 'left' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: a.id === 'logo_white' ? BRAND.blue : BRAND.surface,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      p: 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={a.src}
                      alt={a.name}
                      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Typography variant="caption" noWrap>
                    {a.name}
                  </Typography>
                </ButtonBase>
              ))}
            </Box>

            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Shapes
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {SHAPES.map((s) => (
                <OptionRow key={s.name} onClick={() => addShape(s)}>
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 18,
                        borderRadius: s.radius ? 0.75 : 0.25,
                        bgcolor: s.fill,
                        border: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {s.name}
                    </Typography>
                  </Stack>
                </OptionRow>
              ))}
            </Stack>

            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary' }}>
              Team library
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
              {assets.map((a) => (
                <ButtonBase
                  key={a.id}
                  onClick={() => addImage(a.r2_url)}
                  sx={{ flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', gap: 0.75, textAlign: 'left' }}
                >
                  <Box
                    component="img"
                    src={a.r2_url}
                    alt={a.name}
                    sx={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      objectFit: 'cover',
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                  <Typography variant="caption" noWrap>
                    {a.name}
                  </Typography>
                </ButtonBase>
              ))}
            </Box>
            {!assets.length && (
              <Typography variant="caption" component="p">
                No team uploads yet. Brand assets above are always available.
              </Typography>
            )}
          </>
        )}

        {tab === 'charts' && (
          <>
            <PanelHeader title="Charts" meta={`${CHART_TYPES.length} types`} />
            <Stack spacing={2}>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={chartType}
                onChange={(_, v: ChartType | null) => v && setChartType(v)}
                aria-label="Chart type"
                sx={{ flexWrap: 'wrap' }}
              >
                {CHART_TYPES.map((c) => (
                  <ToggleButton key={c.id} value={c.id} sx={{ flex: '1 1 30%', fontSize: 11, px: 0.5 }}>
                    <c.icon sx={{ fontSize: 14, mr: 0.4 }} />
                    {c.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <TextField
                label="Data (CSV)"
                multiline
                minRows={8}
                value={csv}
                onChange={(e) => setCsv(e.target.value)}
                helperText="First row is the header: label, value"
                slotProps={{
                  input: { sx: { fontFamily: '"JetBrains Mono", monospace', fontSize: 12 } },
                }}
              />
              <Button variant="contained" onClick={parseCsv}>
                Insert chart
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
